import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ResponsiveContainer,
} from "recharts";
import {
  HeartPulse,
  Cpu,
  Palette,
  Scale,
  TrendingUp,
  Sparkles,
  RefreshCcw,
  ArrowUpRight,
} from "lucide-react";
import Navbar from "../components/Navbar";
import api, { formatApiErrorDetail } from "../lib/api";

const ICONS = {
  healthcare: HeartPulse,
  technology: Cpu,
  arts_culture: Palette,
  law_governance: Scale,
  commerce_economy: TrendingUp,
};
const ACCENTS = {
  healthcare: "#10B981",
  technology: "#06B6D4",
  arts_culture: "#F59E0B",
  law_governance: "#4F46E5",
  commerce_economy: "#64748B",
};

export default function Results() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [assessment, setAssessment] = useState(null);
  const [error, setError] = useState("");
  const [insights, setInsights] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(true);
  const [insightsError, setInsightsError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/assessments/${id}`);
        setAssessment(data);
      } catch (e) {
        setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      }
    })();
  }, [id]);

  useEffect(() => {
    if (!assessment) return;
    (async () => {
      setInsightsLoading(true);
      setInsightsError("");
      try {
        const { data } = await api.post("/ai/insights/cached", {
          assessment_id: assessment.id,
        });
        setInsights(data.insights);
      } catch (e) {
        setInsightsError(
          formatApiErrorDetail(e.response?.data?.detail) ||
            "AI insights are taking a moment. Try refreshing.",
        );
      } finally {
        setInsightsLoading(false);
      }
    })();
  }, [assessment]);

  if (error)
    return (
      <div className="App">
        <Navbar />
        <p className="p-12 text-red-600 font-body" data-testid="results-error">
          {error}
        </p>
      </div>
    );

  if (!assessment)
    return (
      <div className="App">
        <Navbar />
        <p className="p-12 font-ui text-xs uppercase tracking-widest text-[var(--es-ink-soft)]">
          Calculating your reading…
        </p>
      </div>
    );

  const radarData = assessment.domain_scores.map((d) => ({
    domain: d.label,
    score: d.score,
    fullMark: 10,
  }));

  return (
    <div className="App" data-testid="results-page">
      <Navbar />

      {/* Header */}
      <section className="border-b border-[var(--es-line)]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24 grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-7">
            <p className="font-ui text-xs tracking-[0.28em] uppercase text-[var(--es-blue)]">
              Your reading · {new Date(assessment.created_at).toLocaleDateString()}
            </p>
            <h1 className="font-display text-5xl sm:text-6xl md:text-7xl text-[var(--es-ink)] tracking-tight mt-3 leading-[1.02]">
              Your Erudition <br /> Score is{" "}
              <em className="text-[var(--es-blue)] not-italic" style={{ fontStyle: "italic" }}>
                {assessment.overall}
              </em>
              <span className="text-[var(--es-ink-soft)] font-display"> / 10</span>
            </h1>
            <p
              className="font-display text-2xl text-[var(--es-blue)] mt-4"
              data-testid="results-tier-label"
            >
              {assessment.tier.label}
            </p>
            <p className="font-body text-[var(--es-ink-soft)] mt-2 max-w-xl">
              {assessment.tier.blurb}
            </p>
          </div>
          <div className="md:col-span-5 flex md:justify-end gap-4">
            <Link
              to="/quiz"
              className="border border-[var(--es-blue)] text-[var(--es-blue)] px-6 py-3 hover:bg-[var(--es-blue-soft)] transition-colors uppercase tracking-wider text-sm font-semibold font-ui inline-flex items-center gap-2"
              data-testid="results-retake-button"
            >
              <RefreshCcw size={14} strokeWidth={1.6} /> Retake
            </Link>
            <Link
              to="/dashboard"
              className="bg-[var(--es-blue)] text-white px-6 py-3 hover:bg-[var(--es-blue-hover)] transition-colors uppercase tracking-wider text-sm font-semibold font-ui inline-flex items-center gap-2"
              data-testid="results-dashboard-button"
            >
              Dashboard <ArrowUpRight size={14} strokeWidth={1.6} />
            </Link>
          </div>
        </div>
      </section>

      {/* Radar + breakdown */}
      <section className="border-b border-[var(--es-line)]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div
            className="lg:col-span-7 border border-[var(--es-line)] p-6 md:p-10 bg-white"
            data-testid="radar-chart-container"
          >
            <p className="font-ui text-[11px] tracking-widest uppercase text-[var(--es-ink-soft)] mb-6">
              Across the five pillars
            </p>
            <div className="h-[420px]">
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={radarData} outerRadius="78%">
                  <PolarGrid stroke="#E2E8F0" />
                  <PolarAngleAxis
                    dataKey="domain"
                    tick={{ fill: "#4B5563", fontSize: 12, fontFamily: "Outfit" }}
                  />
                  <PolarRadiusAxis
                    angle={90}
                    domain={[0, 10]}
                    tick={{ fill: "#9CA3AF", fontSize: 10 }}
                    tickCount={6}
                    stroke="#E2E8F0"
                  />
                  <Radar
                    dataKey="score"
                    stroke="#4267B4"
                    fill="#4267B4"
                    fillOpacity={0.18}
                    strokeWidth={2}
                  />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="lg:col-span-5 grid grid-cols-1 gap-3">
            {assessment.domain_scores.map((d) => {
              const Icon = ICONS[d.key] || Sparkles;
              const accent = ACCENTS[d.key] || "#4267B4";
              return (
                <div
                  key={d.key}
                  className="border border-[var(--es-line)] bg-white p-5 flex items-center gap-5"
                  data-testid={`results-domain-${d.key}`}
                >
                  <div
                    className="w-12 h-12 flex items-center justify-center shrink-0"
                    style={{ background: accent + "1A", color: accent }}
                  >
                    <Icon size={22} strokeWidth={1.5} />
                  </div>
                  <div className="flex-1">
                    <p className="font-display text-xl text-[var(--es-ink)]">{d.label}</p>
                    <div className="mt-1 h-[3px] bg-[var(--es-line)] relative">
                      <div
                        className="absolute left-0 top-0 h-full"
                        style={{ width: `${d.score * 10}%`, background: accent }}
                      />
                    </div>
                  </div>
                  <p className="font-display text-3xl text-[var(--es-blue)] w-16 text-right">
                    {d.score.toFixed(1)}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* AI Insights */}
      <section className="border-b border-[var(--es-line)] bg-[var(--es-bg-soft)]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <div
            className="grid grid-cols-1 md:grid-cols-12 gap-10"
            data-testid="ai-insights-section"
          >
            <div className="md:col-span-4">
              <p className="font-ui text-xs tracking-[0.28em] uppercase text-[var(--es-blue)]">
                Your growth plan
              </p>
              <h2 className="font-display text-4xl sm:text-5xl text-[var(--es-ink)] tracking-tight mt-3 leading-[1.05]">
                A personal <br /> <em className="text-[var(--es-blue)]">cartography</em> of next steps.
              </h2>
              <p className="font-body text-[var(--es-ink-soft)] mt-6">
                Composed by Claude Sonnet 4.5 from your distribution of scores. Editorial in tone,
                concrete in action.
              </p>
            </div>

            <div className="md:col-span-8 bg-white border border-[var(--es-line)] p-8 md:p-12 min-h-[300px]">
              {insightsLoading && (
                <p className="font-ui uppercase text-xs tracking-widest text-[var(--es-ink-soft)]">
                  Composing your plan…
                </p>
              )}
              {insightsError && (
                <p className="text-red-600 font-body" data-testid="ai-insights-error">
                  {insightsError}
                </p>
              )}
              {insights && (
                <article className="fade-in" data-testid="ai-insights-content">
                  <p className="font-display text-2xl md:text-3xl leading-snug text-[var(--es-ink)]">
                    {insights.headline}
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-10">
                    <div>
                      <p className="font-ui text-[11px] tracking-widest uppercase text-[var(--es-blue)]">
                        Strengths
                      </p>
                      <ul className="mt-3 space-y-2 list-disc pl-5 font-body text-[var(--es-ink)]">
                        {(insights.strengths || []).map((s, i) => (
                          <li key={i}>{s}</li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <p className="font-ui text-[11px] tracking-widest uppercase text-[var(--es-blue)]">
                        Growth areas
                      </p>
                      <ul className="mt-3 space-y-2 list-disc pl-5 font-body text-[var(--es-ink)]">
                        {(insights.growth_areas || []).map((s, i) => (
                          <li key={i}>{s}</li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  <div className="mt-10">
                    <p className="font-ui text-[11px] tracking-widest uppercase text-[var(--es-blue)]">
                      30-day plan
                    </p>
                    <ol className="mt-4 divide-y divide-[var(--es-line)] border-t border-[var(--es-line)]">
                      {(insights.thirty_day_plan || []).map((p, i) => (
                        <li
                          key={i}
                          className="grid grid-cols-12 gap-4 py-4 items-start"
                          data-testid={`ai-plan-item-${i}`}
                        >
                          <span className="col-span-1 font-display text-xl text-[var(--es-blue)]">
                            {String(i + 1).padStart(2, "0")}
                          </span>
                          <span className="col-span-3 font-ui text-xs uppercase tracking-widest text-[var(--es-ink-soft)] pt-1.5">
                            {p.domain}
                          </span>
                          <span className="col-span-8 font-body text-[var(--es-ink)]">
                            {p.action}
                          </span>
                        </li>
                      ))}
                    </ol>
                  </div>

                  {insights.reflection_prompt && (
                    <div className="mt-10 border-t border-[var(--es-line)] pt-8">
                      <p className="font-ui text-[11px] tracking-widest uppercase text-[var(--es-blue)]">
                        Reflect this week
                      </p>
                      <p className="font-display italic text-2xl text-[var(--es-ink)] mt-3 leading-snug">
                        "{insights.reflection_prompt}"
                      </p>
                    </div>
                  )}
                </article>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
